/**
 * Pure helpers behind the title page's action row — the single place the
 * viewer's status + rating live on a title page, so status renders exactly
 * once (docs/design.md §5.7, app `components/show/tracking-model.ts`).
 */

import type { TitleType, UserShowStatus } from '@shared/types';

export type SelectableStatus = Exclude<UserShowStatus, 'removed'>;

/** Fixed sheet order — there are exactly four library statuses. */
export const STATUS_ORDER: SelectableStatus[] = [
  'watchlist',
  'watching',
  'completed',
  'dropped',
];

/** Keys into the strings catalog — resolve with `t()`. */
export const STATUS_LABEL_KEYS: Record<SelectableStatus, string> = {
  watchlist: 'show.tracking.status.wantToWatch',
  watching: 'show.tracking.status.watching',
  completed: 'show.tracking.status.finished',
  dropped: 'show.tracking.status.dropped',
};

/** Titles without a status (or removed) render the Add-to-library CTA. */
export function isInLibrary(status: UserShowStatus | null | undefined): boolean {
  return status !== null && status !== undefined && status !== 'removed';
}

/** Movies read a completed title as "Watched". Returns a catalog key. */
export function statusLabelKeyFor(type: TitleType, status: SelectableStatus): string {
  if (type === 'movie' && status === 'completed') return 'show.tracking.status.watched';
  return STATUS_LABEL_KEYS[status];
}

/** Star taps: tapping the current rating clears it, any other sets it. */
export function nextRatingValue(current: number | null | undefined, tapped: number): number | null {
  return current === tapped ? null : tapped;
}

export function progressPercent(watched: number, total: number): number {
  return total > 0 ? Math.round((watched / total) * 1000) / 10 : 0;
}

/** The "N episodes left" caption key — it sits with progress, not status. */
export function episodesLeftKey(left: number): string {
  return left === 1 ? 'show.tracking.episodesLeft_singular' : 'show.tracking.episodesLeft';
}

export type NextActionKind =
  | 'markWatched'
  | 'watchAgain'
  | 'markFinished'
  | 'startEpisode'
  | 'continueEpisode';

export interface NextAction {
  kind: NextActionKind;
  /** Key into the strings catalog; params interpolated by the caller. */
  labelKey: string;
  /** Interpolation params (e.g. the S#E# target). */
  params?: Record<string, string | number>;
}

/**
 * Pure selection of the primary next action — the one element that owns the
 * accent. Movies: mark watched / watch again. Series: start / continue the
 * next episode, or mark finished once all episodes are watched.
 */
export function resolveNextAction(input: {
  type: TitleType;
  status: UserShowStatus | null;
  watchedCount: number;
  totalEpisodes: number;
  nextEpisode?: { season: number; episode: number } | null;
  /** The episode code in the label ("S1·E7"); the caller localizes it. */
  formatCode?: (season: number, episode: number) => string;
}): NextAction | null {
  if (!isInLibrary(input.status)) return null;
  if (input.type === 'movie') {
    return input.status === 'completed'
      ? { kind: 'watchAgain', labelKey: 'show.action.watchAgain' }
      : { kind: 'markWatched', labelKey: 'show.action.markWatched' };
  }
  const allWatched = input.totalEpisodes > 0 && input.watchedCount >= input.totalEpisodes;
  if (allWatched || input.totalEpisodes === 0) {
    return input.status === 'completed'
      ? { kind: 'watchAgain', labelKey: 'show.action.watchAgain' }
      : { kind: 'markFinished', labelKey: 'show.action.markFinished' };
  }
  const format = input.formatCode ?? ((season, episode) => `S${season}E${episode}`);
  const target = input.nextEpisode
    ? format(input.nextEpisode.season, input.nextEpisode.episode)
    : '';
  if (input.watchedCount === 0) {
    return target
      ? { kind: 'startEpisode', labelKey: 'show.action.start', params: { target } }
      : { kind: 'startEpisode', labelKey: 'show.action.startWatching' };
  }
  return target
    ? { kind: 'continueEpisode', labelKey: 'show.action.continueLabel', params: { target } }
    : { kind: 'continueEpisode', labelKey: 'show.action.continueShort' };
}

export type HeroAction =
  | { kind: 'addToWatchlist' }
  | { kind: 'rate' }
  | (NextAction & { tone: 'accent' | 'neutral' });

/**
 * The hero's ONE primary (screens.md §/show/:id): not in the library → Add to
 * watchlist; completed and unrated → the rating itself; completed and rated
 * → Watch again, neutral (nothing is urgent); otherwise the next action
 * (Mark watched / Start / Continue / Mark finished) in amber.
 */
export function resolveHeroAction(
  input: Parameters<typeof resolveNextAction>[0] & { rating?: number | null },
): HeroAction | null {
  if (!isInLibrary(input.status)) return { kind: 'addToWatchlist' };
  if (input.status === 'completed' && !input.rating) return { kind: 'rate' };
  const next = resolveNextAction(input);
  return next ? { ...next, tone: next.kind === 'watchAgain' ? 'neutral' : 'accent' } : null;
}
